import React, { useState, useRef, useEffect } from 'react';
import { Heading2, ChevronDown, Check } from './icons';

export function H2DropdownSelector({ editor }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!editor) return;
    const handleUpdate = () => setTick((t) => t + 1);
    editor.on('transaction', handleUpdate);
    editor.on('selectionUpdate', handleUpdate);
    return () => {
      editor.off('transaction', handleUpdate);
      editor.off('selectionUpdate', handleUpdate);
    };
  }, [editor]);

  if (!editor) return null;

  const isH2Active = editor.isActive('heading', { level: 2 });
  const isHeading = editor.isActive('heading');
  const isBothActive = !isHeading && editor.isActive('bold') && editor.isActive('underline');

  const handleToggleH2 = (e) => {
    if (e) e.preventDefault();
    if (!editor) return;
    editor.chain().focus().toggleHeading({ level: 2 }).run();
    setIsOpen(false);
  };

  const handleBoldAndUnderline = (e) => {
    if (e) e.preventDefault();
    if (!editor) return;

    const { selection } = editor.state;
    const isCurrentHeading = editor.isActive('heading');
    const isCurrentBoth = !isCurrentHeading && editor.isActive('bold') && editor.isActive('underline');

    if (isCurrentHeading) {
      if (selection.empty) {
        const $from = selection.$from;
        const start = $from.start();
        const end = $from.end();
        editor
          .chain()
          .focus()
          .setTextSelection({ from: start, to: end })
          .setParagraph()
          .setBold()
          .setUnderline()
          .run();
      } else {
        editor
          .chain()
          .focus()
          .setParagraph()
          .setBold()
          .setUnderline()
          .run();
      }
    } else if (isCurrentBoth) {
      editor.chain().focus().unsetBold().unsetUnderline().run();
    } else {
      editor.chain().focus().setBold().setUnderline().run();
    }

    setIsOpen(false);
  };

  return (
    <div
      className="h2-selector-container"
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      <div
        className={`toolbar-btn-group ${isH2Active ? 'is-active' : ''}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: '6px',
          border: '1px solid transparent',
          backgroundColor: isH2Active ? 'var(--accent-hover, #eff6ff)' : 'transparent',
        }}
      >
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          className={`toolbar-btn ${isH2Active ? 'is-active' : ''}`}
          onClick={handleToggleH2}
          title="H2: Bold + Centre (##)"
          style={{
            padding: '4px 6px',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '32px',
          }}
        >
          <Heading2 size={18} />
        </button>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          className={`toolbar-btn ${isOpen ? 'is-active' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          title="H2 Options: Bold + Centre (##) or Bold & Underline (__**yoga**__)"
          style={{
            padding: '4px 3px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: '1px solid rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '32px',
          }}
        >
          <ChevronDown size={12} style={{ opacity: 0.7 }} />
        </button>
      </div>

      {isOpen && (
        <div
          className="h2-options-popover"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '6px',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            zIndex: 1000,
            width: '260px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '2px 6px 4px 6px',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Heading 2 / Style Options
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px' }}>
            {/* Option 1: H2: Bold + Centre */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleToggleH2}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 10px',
                borderRadius: '6px',
                border: isH2Active ? '1.5px solid #2563eb' : '1px solid #f1f5f9',
                backgroundColor: isH2Active ? '#eff6ff' : '#f8fafc',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (!isH2Active) e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                if (!isH2Active) e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: isH2Active ? '#1e40af' : '#334155',
                  }}
                >
                  Bold + Centre
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
                  ##text
                </div>
              </div>
              {isH2Active && <Check size={16} color="#2563eb" style={{ flexShrink: 0 }} />}
            </button>

            {/* Option 2: Bold & Underline */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleBoldAndUnderline}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 10px',
                borderRadius: '6px',
                border: isBothActive ? '1.5px solid #2563eb' : '1px solid #f1f5f9',
                backgroundColor: isBothActive ? '#eff6ff' : '#f8fafc',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (!isBothActive) e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                if (!isBothActive) e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: isBothActive ? '#1e40af' : '#334155',
                  }}
                >
                  Bold &amp; Underline
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
                  __**text**__
                </div>
              </div>
              {isBothActive && <Check size={16} color="#2563eb" style={{ flexShrink: 0 }} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
